"use client";
import { jsx } from "react/jsx-runtime";
import { forwardRef } from "react";
import { useTracking } from "react-tracking";
import { INVALID_PROPS_ERROR, COMPONENT_NAME } from "./constants.js";
import { StyledButton } from "./styles.js";
import { VARIANTS, LOADING_POSITION } from "./types.js";
import { LoadingButton } from "./view.loading.js";
import { StandardButton } from "./view.standard.js";
import { getDataTestId } from "../../Util/index.js";
const Button = forwardRef(
  (props, ref) => {
    const {
      variant = VARIANTS.PRIMARY,
      children,
      onClick,
      onMouseOver,
      onKeyDown,
      isLoading = false,
      loadingPosition = "start",
      startIcon,
      sx,
      href,
      target,
      "aria-labelledby": ariaLabelledBy,
      "aria-describedby": ariaDescribedBy,
      "aria-label": ariaLabel,
      "aria-pressed": ariaPressed,
      uniqueId,
      form,
      type,
      fullWidth,
      ...rest
    } = props;
    const generatedId = getDataTestId(
      COMPONENT_NAME,
      uniqueId ?? "button-default"
    );
    const { trackEvent } = useTracking();
    const onClickHandler = (event) => {
      trackEvent();
      onClick == null ? void 0 : onClick(event);
    };
    const onKeyDownHandler = (event) => {
      onKeyDown == null ? void 0 : onKeyDown(event);
    };
    if (!Object.values(VARIANTS).includes(variant)) {
      console.error(
        `${INVALID_PROPS_ERROR} Invalid prop 'variant' of value '${variant}' supplied to Button, expected one of ['primary', 'primary-alternate', 'secondary', 'tertiary', 'tertiary-dark-bg', 'ghost', 'ghost-dark-bg', 'text-button', 'text-button-alternate', 'text-button-out-of-stock'].`
      );
      return null;
    }
    if (!LOADING_POSITION.includes(loadingPosition)) {
      console.error(
        `${INVALID_PROPS_ERROR} Invalid prop 'loadingPosition' of value '${loadingPosition}' supplied to Button, expected one of ['start', 'end', 'center'].`
      );
      return null;
    }
    if (!children) {
      console.error(
        `${INVALID_PROPS_ERROR} Missing required children prop.`
      );
      return null;
    }
    return /* @__PURE__ */ jsx(
      StyledButton,
      {
        id: generatedId,
        "data-testid": getDataTestId(COMPONENT_NAME, uniqueId),
        variant,
        onClick: onClickHandler,
        onMouseOver,
        onKeyDown: onKeyDownHandler,
        startIcon,
        isLoading,
        loadingPosition,
        disabled: isLoading,
        sx,
        href,
        target,
        ref,
        "aria-labelledby": ariaLabelledBy,
        "aria-describedby": ariaDescribedBy,
        "aria-label": ariaLabel,
        "aria-pressed": ariaPressed,
        form,
        type,
        fullWidth,
        ...rest,
        children: isLoading ? /* @__PURE__ */ jsx(
          LoadingButton,
          {
            variant,
            loadingPosition,
            children
          }
        ) : /* @__PURE__ */ jsx(StandardButton, { children })
      }
    );
  }
);
Button.displayName = COMPONENT_NAME;
export {
  Button
};
